import dayjs, { Dayjs } from "dayjs";
import { AuthenticationFactorStatus, DelegateRole, Factor, Traveler, User, UserRole, UserType } from "types";

export const isXolaAdmin = (user: User) => {
    return user?.roles?.includes(UserRole.ADMIN) ?? false;
};

// Ids of the travelers merged into this survivor (references stored in meta.mergedTravelers). The CRM
// hydrates them via GET /users?id[in]=; ids that no longer resolve are legacy (source deleted) and skipped.
export const getMergedTravelerIds = (contact: Traveler): string[] =>
    Object.values(contact.meta?.mergedTravelers ?? {}).map((entry) => entry.id);

// A contact shows the "Merged Record" badge whenever it absorbed any record.
export const isMergedRecord = (contact: Traveler) => Object.keys(contact.meta?.mergedTravelers ?? {}).length > 0;

// True when the contact is itself merged away; unmerge is reverse-order, so its own absorbed records
// stay locked until this record is restored first.
export const isMergedAway = (contact: Traveler) => Boolean(contact.mergedInto?.id);

/**
 * @param forceCheckRole param to skip checks for Admin and Seller,
 * for cases when we still need to hide some functionality (e.g. the Settings -> Guide Profile page)
 */

export const hasRole = (user: User, role: UserRole | DelegateRole, forceCheckRole = false) => {
    // Xola admins are allowed to see everything.
    if (user.type === UserType.USER && user.roles.includes(UserRole.ADMIN) && !forceCheckRole) {
        return true;
    }

    // This should be fine since a seller account can't be a delegate of another seller.
    if (user.type === UserType.SELLER && !forceCheckRole) {
        return true;
    }

    if (user.roles.includes(DelegateRole.SELLER_ADMIN) && !forceCheckRole) {
        return true;
    }

    // TODO: @srishaharidas There is a weird API bug we need to check. When the user is a delegate with only one role (ROLE_CURATOR),
    // the seller response will not contain the roles array.
    return user.roles?.includes(role) ?? false;
};

export const hasAnyRole = (user: User, roles: Array<UserRole | DelegateRole>) => {
    return roles.some((role) => hasRole(user, role));
};

export const isMinor = (date: string | Date | Dayjs, minorAgeLimit?: number) => {
    const dob = dayjs(date);
    const yearsAgoLimit = dayjs().subtract(minorAgeLimit ?? 18, "year");
    return dob.isAfter(yearsAgoLimit);
};

export const isAffiliate = (user: User) => {
    return user.roles?.includes(DelegateRole.AFFILIATE) ?? false;
};

export const hasOnlyRoles = (user: User, roles: Array<UserRole | DelegateRole>) => {
    return roles.every((role) => hasRole(user, role)) && user.roles.length === roles.length;
};

// Check roles directly, not via hasRole/hasOnlyRoles: hasRole short-circuits true for
// seller/admin/seller_admin accounts, which would misclassify any single-role privileged user.
export const isWareManagerOnly = (user: User) =>
    user.roles.length === 1 && user.roles.includes(DelegateRole.WARE_MANAGER);

export const isMultiFactorAuthenticationEnabled = (user: User): boolean => {
    // For Users Roles other then Seller
    if (!user.preferences?.multiFactorAuthentication) {
        return false;
    }

    const defaultAuthenticationFactor = getDefaultAuthenticationFactor(user);
    return (
        !!user.preferences.multiFactorAuthentication?.enabled &&
        defaultAuthenticationFactor?.status === AuthenticationFactorStatus.VERIFIED
    );
};

export const getDefaultAuthenticationFactor = (user: User): Factor | undefined => {
    if (!user.preferences?.multiFactorAuthentication?.enabled) {
        return undefined;
    }

    const defaultFactorId = user.preferences.multiFactorAuthentication?.defaultFactor;
    return user.preferences.multiFactorAuthentication?.factors?.find(
        (factor: Factor) => factor?.id === defaultFactorId,
    );
};

export const getAuthenticationFactorByPhone = (user: User, phone: string): Factor | undefined => {
    return user.preferences?.multiFactorAuthentication?.factors?.find((factor: Factor) => factor?.phone === phone);
};

export const getAuthenticationFactor = (user: User, factorId?: string): Factor | undefined => {
    return user.preferences?.multiFactorAuthentication?.factors?.find((factor: Factor) => factor?.id === factorId);
};

export const getLocale = (user: User) => {
    return user.locale?.replaceAll("-", "_");
};
